/**
 * 36_CUSTOM / _INTRO §8 — les clés de cache des champs personnalisés.
 *
 * ⭐ **L'entité entre dans CHAQUE clé.** Les sept routes exigent `?entity_id=`, et la lecture
 * rend les définitions VUES depuis cette entité : les siennes ET celles de ses ancêtres. Deux
 * organismes ne lisent donc pas la même liste. Une clé sans l'entité servirait, le temps du
 * `staleTime`, les champs de l'organisme qu'on vient de quitter à celui qu'on ouvre.
 * 🔴 La première prescription (CUSTOM-001 §7 : `queryKeys.core.ts`, `['field-config', target]`)
 * n'avait pas d'entité : elle a été écrite avant que le contrat des routes soit mesuré.
 *
 * ⭐ **`all` est le PRÉFIXE d'invalidation, jamais une clé de lecture.** Toute écriture invalide
 * l'espace entier (`useFieldConfig`) : une retouche de champ livré change la lecture par cible
 * ET celle des champs livrés, un champ créé change aussi l'inventaire du hub. Viser la clé
 * exacte de chaque lecture finit toujours par en oublier une.
 *
 * ⚠️ Forme `['custom-fields', <verbe>, ...<ids>]`. Le registre PUBLIC reste `@/lib/queryKeys`,
 * qui étale cet objet : on écrit `queryKeys.customFields.*`, la forme que lit le gardien
 * `toute-cle-de-requete-appelee-est-declaree`.
 */
import type { CustomFieldTarget } from '@/types/custom-fields'

const customFieldsAll = ['custom-fields'] as const
const CHAMPS_MANQUANTS = 'required-fields'

export const customFieldsKeys = {
    customFields: {
        all: customFieldsAll,
        /** `GET ?target=` : les définitions d'UNE fiche, SANS `usage_count` — ce que lit la fiche. */
        definitions: (target: CustomFieldTarget, entityId: string) =>
            [...customFieldsAll, 'definitions', target, entityId] as const,
        /**
         * `GET ?target=&with_usage=true` : les mêmes, `usage_count` compris — l'écran des champs et
         * la fenêtre « Modifier le champ ». ⚠️ Une clé À PART : la réponse n'a pas la même forme,
         * et le balayage des fiches qu'elle coûte ne se paie pas à chaque ouverture d'une fiche.
         */
        definitionsAvecUsage: (target: CustomFieldTarget, entityId: string) =>
            [...customFieldsAll, 'definitions-avec-usage', target, entityId] as const,
        /**
         * `GET ?target=&consolidee=true` : ce que voit une liste CONSOLIDÉE (l'annuaire) — la
         * chaîne montante, puis les définitions des descendantes. Une clé À PART : ce n'est pas
         * ce que lit la fiche.
         */
        definitionsConsolidees: (target: CustomFieldTarget, entityId: string) =>
            [...customFieldsAll, 'definitions-consolidees', target, entityId] as const,
        /**
         * `GET` SANS `target` : l'inventaire des cibles servies, qui nourrit le « OU » de la
         * carte du hub. ⚠️ Un verbe À PART, pas une cible de plus sous `definitions` : sa
         * réponse n'a pas la même forme (aucun `usage_count`), et deux formes sous un même
         * préfixe finissent lues l'une pour l'autre.
         */
        toutesCibles: (entityId: string) => [...customFieldsAll, 'toutes-cibles', entityId] as const,
        /** `GET /{id}/usage` : le compte d'usage d'UN champ — la fenêtre « Modifier le champ ». */
        usage: (definitionId: string, entityId: string) =>
            [...customFieldsAll, 'usage', definitionId, entityId] as const,
        /** `GET /native/{target}` : les champs livrés retouchables et leur retouche. */
        native: (target: CustomFieldTarget, entityId: string) =>
            [...customFieldsAll, 'native', target, entityId] as const,
        /**
         * ⚠️ Une clé de MUTATION, pas de lecture : le rangement en cours d'un organisme. Elle
         * sert à compter les rangements encore en vol (`isMutating`) — la liste ne se relit
         * qu'après le DERNIER, sinon la relecture d'un premier dépôt repeindrait l'ordre
         * d'avant par-dessus le second, déjà posé à l'écran.
         */
        rangement: (entityId: string) => [...customFieldsAll, 'rangement', entityId] as const,
        /**
         * ⚠️ Un SEGMENT, pas un préfixe : le badge « Champs manquants » d'une fiche range sa
         * lecture SOUS la clé de la fiche (son enregistrement l'invalide ainsi), et l'écriture
         * d'une définition le relit en cherchant ce segment dans toute clé
         * (`relireLesChampsManquants`). ⛔ Un élément, jamais un préfixe de texte : les aperçus
         * de création (`'required-fields-preview'`) ne sont pas des badges.
         */
        SEGMENT_DES_CHAMPS_MANQUANTS: CHAMPS_MANQUANTS,
        /** `GET …/{id}/required-fields` : ce qui manque à UNE fiche, vu depuis l'entité du lecteur. */
        champsManquants: (cleDeLaFiche: readonly unknown[], entityId: string) =>
            [...cleDeLaFiche, CHAMPS_MANQUANTS, entityId] as const,
    },
}
